import React from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as InfoIcon } from '../../../../../../assets/icons/v3/info-v2.svg';
import BdsTooltip from '../../../../../../shared/components/v3/BdsTooltip/BdsTooltip';
import { FLEXIBLE_REMUNERATION_BENEFITS } from '../../../../../../shared/constants/flexibleRemuneration';
import { IMemberFRConsumptionItem } from '../../../../../../shared/interfaces/collaborators';
import { TRANSLATION_COLLABORATORS } from '../../../../../../shared/translations/collaborators';
import { toLocaleNumberFormat } from '../../../../../../shared/utils/utils';

import './ConsumptionTable.scss';

interface IConsumptionTable {
  rows: IMemberFRConsumptionItem[];
  totalExempt: number;
  totalNonExempt: number;
  total: number;
  currency: string;
}

const formatAmount = (value: number, currency: string): string =>
  value === 0
    ? '—'
    : toLocaleNumberFormat(
        value,
        {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
          maximumFractionDigits: 2
        },
        'es-ES'
      );

const ConsumptionTable = ({ rows, totalExempt, totalNonExempt, total, currency }: IConsumptionTable) => {
  const { t } = useTranslation();
  const { table } = TRANSLATION_COLLABORATORS.detail.collaboratorBalanceRF;

  const getBenefitName = (sku: string) =>
    t(FLEXIBLE_REMUNERATION_BENEFITS[sku as keyof typeof FLEXIBLE_REMUNERATION_BENEFITS]?.title ?? sku);

  return (
    <div className="consumption-table-scroll">
      <table className="consumption-table">
        <colgroup>
          <col />
          <col className="consumption-table-col-amount" />
          <col className="consumption-table-col-amount" />
          <col className="consumption-table-col-amount" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col" className="consumption-table-th">
              {t(table.benefit)}
            </th>
            <th scope="col" className="consumption-table-th consumption-table-amount">
              {t(table.exempt)}
            </th>
            <th scope="col" className="consumption-table-th consumption-table-amount">
              <span className="consumption-table-th-help">
                {t(table.nonExempt)}
                <BdsTooltip message={table.nonExemptHelp} position="bottom-right">
                  <InfoIcon className="consumption-table-info-icon" />
                </BdsTooltip>
              </span>
            </th>
            <th scope="col" className="consumption-table-th consumption-table-amount consumption-table-total">
              {t(table.total)}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.sku}>
              <td className="consumption-table-td">{getBenefitName(row.sku)}</td>
              <td className="consumption-table-td consumption-table-amount">{formatAmount(row.exempt, currency)}</td>
              <td className="consumption-table-td consumption-table-amount">{formatAmount(row.nonExempt, currency)}</td>
              <td className="consumption-table-td consumption-table-amount consumption-table-total">
                {formatAmount(row.total, currency)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className="consumption-table-foot">{t(table.total)}</td>
            <td className="consumption-table-foot consumption-table-amount">{formatAmount(totalExempt, currency)}</td>
            <td className="consumption-table-foot consumption-table-amount">
              {formatAmount(totalNonExempt, currency)}
            </td>
            <td className="consumption-table-foot consumption-table-amount consumption-table-total">
              {formatAmount(total, currency)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default ConsumptionTable;
